import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLeadClock } from './LeadClockProvider';
import { AnimatedNumber } from './AnimatedNumber';
const LAYOUT_ID = 'lead-clock-card';
export function LeadClockCapsule() {
  const {
    probability,
    status,
    theme,
    interpretation,
    timerLabel,
    reset
  } = useLeadClock();
  const reduceMotion = useReducedMotion();
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const isLost = status === 'LOST';
  return <motion.div layoutId={LAYOUT_ID} layout transition={reduceMotion ? {
    duration: 0
  } : {
    type: 'spring',
    stiffness: 240,
    damping: 28,
    mass: 0.9
  }} onMouseEnter={() => setShowTimer(true)} onMouseLeave={() => setShowTimer(false)} onFocus={() => setShowTimer(true)} onBlur={() => setShowTimer(false)} tabIndex={0} role="timer" aria-label={`Lead status ${status}, ${Math.round(probability)}% chance, elapsed ${timerLabel}`} className="relative inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-gray-300 bg-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2" data-id="element-302">
      {/* Pulse dot — always visible */}
      <span className="relative inline-flex w-2 h-2 flex-shrink-0" data-id="element-303">
        {!reduceMotion && <span className={`absolute inset-0 rounded-full ${theme.dot} opacity-60 animate-ping transition-colors duration-500`} data-id="element-304" />}
        <span className={`relative w-2 h-2 rounded-full ${theme.dot} transition-colors duration-500`} data-id="element-305" />
      </span>

      {/* Timer — hover/focus only */}
      <AnimatePresence initial={false} data-id="element-306">
        {showTimer && <motion.span key="timer" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        width: 0,
        marginLeft: -8
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        width: 'auto',
        marginLeft: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        width: 0,
        marginLeft: -8
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="overflow-hidden whitespace-nowrap text-xs font-mono font-semibold tabular-nums text-gray-900" data-id="element-307">
            {timerLabel}
          </motion.span>}
      </AnimatePresence>

      {/* Sep + probability — hidden under 480px */}
      <span className="text-gray-300 text-xs hidden xs:inline" aria-hidden data-id="element-308">
        ·
      </span>
      <button type="button" onMouseEnter={() => setShowInterpretation(true)} onMouseLeave={() => setShowInterpretation(false)} onFocus={() => setShowInterpretation(true)} onBlur={() => setShowInterpretation(false)} className={`hidden xs:inline text-xs font-mono font-semibold tabular-nums cursor-help focus:outline-none transition-colors duration-500 ${theme.text}`} data-id="element-309">
        <AnimatedNumber value={probability} duration={500} data-id="element-310" />%
      </button>

      {/* Sep + status — hidden under 720px */}
      <span className="text-gray-300 text-xs hidden sm:inline" aria-hidden data-id="element-311">
        ·
      </span>
      <AnimatePresence mode="wait" initial={false} data-id="element-312">
        <motion.span key={`status-${status}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 3
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -3
      }} transition={{
        duration: 0.24,
        ease: 'easeOut'
      }} className={`hidden sm:inline text-[11px] font-mono font-semibold tracking-wider transition-colors duration-500 ${theme.text}`} data-id="element-313">
          {status}
        </motion.span>
      </AnimatePresence>

      {/* Reset shows once LOST */}
      {isLost && <button type="button" onClick={reset} aria-label="Reset clock" className="ml-1 text-xs text-gray-500 hover:text-gray-900 transition-colors" data-id="element-314">
          ↺
        </button>}

      {/* Plain-language interpretation tooltip */}
      <AnimatePresence data-id="element-315">
        {showInterpretation && <motion.div key={`tip-${status}`} role="tooltip" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="absolute top-full right-0 mt-2 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg pointer-events-none z-50" data-id="element-316">
            {interpretation}
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}